/**
 * Dashboard — operational overview with real data.
 * Shows fleet health, mobile devices, employees, circuits, sites,
 * and data validation alerts.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface DashboardData {
  assets: {
    total: number; inUse: number; inventory: number;
    laptops: number; desktops: number;
    ninjaOnline: number; ninjaOffline: number;
    intuneCompliant: number; intuneNonCompliant: number;
  };
  mobile: { total: number; online: number; offline: number };
  peripherals: { total: number };
  employees: { total: number; sites: number };
  circuits: { total: number; totalMRC: number };
  sites: { totalActive: number };
  dataValidation: { highCount: number; mediumCount: number; lowCount: number };
}

interface FleetSummary {
  summary: { totalComputers: number; avgAge: number | null; overdueRefresh: number; totalWithAge: number };
}

interface BranchFleetScore {
  siteNumber: string;
  branchName: string;
  deviceCount: number;
  avgScore: number;
  avgAge: number;
  devicesNeedingReplacement: number;
}

interface FleetPriorityData {
  branchScores: BranchFleetScore[];
}

interface TicketPulse {
  ticketsToday: number;
  ticketsThisWeek: number;
  staleCount: number;
}

interface EmployeeChanges {
  summary: { newHires: number; terminations?: number; departures?: number; changes?: number; roleChanges?: number };
  changes?: Array<{ ticketId?: number; employeeId?: number; title?: string; name?: string; changeType: string; requestorName?: string; createdDate?: string; hireDate?: string | null; status?: string; location?: string | null; jobTitle?: string | null; site?: string | null }>;
}

/** Normalize various date formats (M.D.YYYY, M/D/YY, full date strings) to MM/DD/YYYY. */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  // Handle M.D.YYYY or M/D/YY formats first (before Date parsing which may misinterpret dots)
  const parts = dateStr.split(/[\/\.]/);
  if (parts.length === 3 && /^\d{1,2}$/.test(parts[0]) && /^\d{1,2}$/.test(parts[1])) {
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    let year = parts[2].trim();
    if (year.length === 2) year = '20' + year;
    if (/^\d{4}$/.test(year)) {
      return `${month}/${day}/${year}`;
    }
  }
  // Try standard Date parsing
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  }
  return dateStr;
}

/** Export data as a CSV file download. */
function exportToCSV(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [fleet, setFleet] = useState<FleetSummary | null>(null);
  const [ticketPulse, setTicketPulse] = useState<TicketPulse | null>(null);
  const [deskPulse, setDeskPulse] = useState<{ today: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number; abandonedRate: number }; previousDay: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number }; thisWeek: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number; abandonedRate: number }; previousWeek: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number }; thisMonth: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number; abandonedRate: number }; previousMonth: { tickets: number; calls: number; callsAnswered: number; callsAbandoned: number }; staleTickets: number; trends: { tickets: { dayOverDay: number; weekOverWeek: number; monthOverMonth: number }; calls: { dayOverDay: number; weekOverWeek: number; monthOverMonth: number } } } | null>(null);
  const [empChanges, setEmpChanges] = useState<EmployeeChanges | null>(null);
  const [branchHealth, setBranchHealth] = useState<BranchFleetScore[]>([]);
  const [empFilter, setEmpFilter] = useState<'all' | 'new_hire' | 'termination' | 'change'>('all');
  const [empDays, setEmpDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch employee changes — called directly when days change
  const fetchEmpChanges = useCallback(async (days: number) => {
    try {
      const res = await fetch(`/api/employees/changes?days=${days}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (res.ok) setEmpChanges(await res.json());
    } catch { /* non-fatal */ }
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, fleetRes, pulseRes, priorityRes, deskPulseRes] = await Promise.all([
        fetch('/api/dashboard', { credentials: 'include' }),
        fetch('/api/fleet-analytics', { credentials: 'include' }),
        fetch('/api/dashboard/ticket-pulse', { credentials: 'include' }),
        fetch('/api/fleet-analytics/replacement-priority', { credentials: 'include' }),
        fetch('/api/service-desk/pulse', { credentials: 'include' }),
      ]);
      if (!dashRes.ok) throw new Error(`HTTP ${dashRes.status}`);
      setData(await dashRes.json());
      if (fleetRes.ok) setFleet(await fleetRes.json());
      if (pulseRes.ok) setTicketPulse(await pulseRes.json());
      if (deskPulseRes.ok) setDeskPulse(await deskPulseRes.json());
      if (priorityRes.ok) {
        const priorityData: FleetPriorityData = await priorityRes.json();
        setBranchHealth(priorityData.branchScores?.slice(0, 5) ?? []);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchEmpChanges(empDays); }, [fetchEmpChanges, empDays]);

  if (loading) return <div className="flex items-center justify-center h-64"><span className="text-slate-500">Loading dashboard…</span></div>;
  if (error) return <div className="p-6"><p className="text-red-600">{error}</p><button onClick={fetchDashboard} className="mt-2 text-sm text-baker-red">Retry</button></div>;
  if (!data) return null;

  const { assets, mobile, employees, circuits, sites, dataValidation } = data;
  const fmtNum = (n: number) => n.toLocaleString();

  return (
    <div className="max-w-6xl">
      <h1 className="text-lg font-semibold text-text-primary mb-6">Dashboard</h1>

      {/* Top KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
        <KPI label="Computers" value={fmtNum(assets.laptops + assets.desktops)} sub={`${fmtNum(assets.laptops)} laptops · ${fmtNum(assets.desktops)} desktops`} link="/assets" />
        <KPI label="Mobile Devices" value={fmtNum(mobile?.total ?? 0)} sub="SOTI managed" link="/assets" />
        <KPI label="Employees" value={fmtNum(employees.total)} sub={`across ${employees.sites} sites`} link="/employees" />
        <KPI label="Physical Sites" value={fmtNum(sites.totalActive)} link="/sites" />
        <KPI label="Active Circuits" value={fmtNum(circuits.total)} link="/circuits" />
      </div>

      {/* Ticket Pulse + Employee Changes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Service Desk Pulse */}
        {(ticketPulse || deskPulse) && (
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center justify-between">
              <Link to="/ticket-insights" className="no-underline text-text-primary hover:text-baker-red">Service Desk Pulse</Link>
              <button
                onClick={(e) => { e.stopPropagation(); exportToCSV('service-desk-pulse', ['Period', 'Tickets', 'Calls', 'Answered', 'Abandoned'], deskPulse ? [['Today', String(deskPulse.today.tickets), String(deskPulse.today.calls), String(deskPulse.today.callsAnswered), String(deskPulse.today.callsAbandoned)], ['This Week', String(deskPulse.thisWeek.tickets), String(deskPulse.thisWeek.calls), String(deskPulse.thisWeek.callsAnswered), String(deskPulse.thisWeek.callsAbandoned)], ['This Month', String(deskPulse.thisMonth.tickets), String(deskPulse.thisMonth.calls), String(deskPulse.thisMonth.callsAnswered), String(deskPulse.thisMonth.callsAbandoned)]] : []); }}
                className="text-slate-400 hover:text-baker-red text-xs px-1.5 py-0.5 rounded hover:bg-slate-50 transition-all"
                title="Export to CSV"
              >📥</button>
            </h2>
            {deskPulse ? (
              <div>
                <table className="w-full text-xs mb-3">
                  <thead>
                    <tr className="border-b border-border text-slate-500">
                      <th className="text-left py-1.5 font-medium"></th>
                      <th className="text-right py-1.5 font-medium">Tickets</th>
                      <th className="text-right py-1.5 font-medium">Calls</th>
                      <th className="text-right py-1.5 font-medium">Answered</th>
                      <th className="text-right py-1.5 font-medium">Abandoned</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="py-1.5 font-medium text-slate-700">Today</td>
                      <td className="py-1.5 text-right">{deskPulse.today.tickets} <TrendArrow current={deskPulse.today.tickets} previous={deskPulse.previousDay.tickets} /></td>
                      <td className="py-1.5 text-right">{deskPulse.today.calls} <TrendArrow current={deskPulse.today.calls} previous={deskPulse.previousDay.calls} /></td>
                      <td className="py-1.5 text-right text-emerald-600">{deskPulse.today.callsAnswered} <TrendArrow current={deskPulse.today.callsAnswered} previous={deskPulse.previousDay.callsAnswered} invert /></td>
                      <td className="py-1.5 text-right text-red-600">{deskPulse.today.callsAbandoned} <TrendArrow current={deskPulse.today.callsAbandoned} previous={deskPulse.previousDay.callsAbandoned} /></td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-1.5 font-medium text-slate-700">This Week</td>
                      <td className="py-1.5 text-right">{deskPulse.thisWeek.tickets} <TrendArrow current={deskPulse.thisWeek.tickets} previous={deskPulse.previousWeek.tickets} /></td>
                      <td className="py-1.5 text-right">{deskPulse.thisWeek.calls} <TrendArrow current={deskPulse.thisWeek.calls} previous={deskPulse.previousWeek.calls} /></td>
                      <td className="py-1.5 text-right text-emerald-600">{deskPulse.thisWeek.callsAnswered} <TrendArrow current={deskPulse.thisWeek.callsAnswered} previous={deskPulse.previousWeek.callsAnswered} invert /></td>
                      <td className="py-1.5 text-right text-red-600">{deskPulse.thisWeek.callsAbandoned} <TrendArrow current={deskPulse.thisWeek.callsAbandoned} previous={deskPulse.previousWeek.callsAbandoned} /></td>
                    </tr>
                    <tr>
                      <td className="py-1.5 font-medium text-slate-700">This Month</td>
                      <td className="py-1.5 text-right">{deskPulse.thisMonth.tickets} <TrendArrow current={deskPulse.thisMonth.tickets} previous={deskPulse.previousMonth.tickets} /></td>
                      <td className="py-1.5 text-right">{deskPulse.thisMonth.calls} <TrendArrow current={deskPulse.thisMonth.calls} previous={deskPulse.previousMonth.calls} /></td>
                      <td className="py-1.5 text-right text-emerald-600">{deskPulse.thisMonth.callsAnswered} <TrendArrow current={deskPulse.thisMonth.callsAnswered} previous={deskPulse.previousMonth.callsAnswered} invert /></td>
                      <td className="py-1.5 text-right text-red-600">{deskPulse.thisMonth.callsAbandoned} <TrendArrow current={deskPulse.thisMonth.callsAbandoned} previous={deskPulse.previousMonth.callsAbandoned} /></td>
                    </tr>
                  </tbody>
                </table>
                {deskPulse.staleTickets > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-red-600 font-bold">{deskPulse.staleTickets}</span>
                    <span className="text-slate-500">stale tickets (&gt;30 days open)</span>
                  </div>
                )}
                {deskPulse.thisWeek.abandonedRate > 0 && (
                  <div className="flex items-center gap-2 text-xs mt-1">
                    <span className={`font-bold ${deskPulse.thisWeek.abandonedRate > 20 ? 'text-red-600' : deskPulse.thisWeek.abandonedRate > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>{deskPulse.thisWeek.abandonedRate}%</span>
                    <span className="text-slate-500">call abandoned rate this week</span>
                  </div>
                )}
              </div>
            ) : ticketPulse ? (
              <div className="flex gap-4">
                <div className="text-center"><div className="text-2xl font-bold">{ticketPulse.ticketsToday}</div><div className="text-xs text-slate-500">Today</div></div>
                <div className="text-center"><div className="text-2xl font-bold">{ticketPulse.ticketsThisWeek}</div><div className="text-xs text-slate-500">This Week</div></div>
                <div className="text-center"><div className={`text-2xl font-bold ${ticketPulse.staleCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{ticketPulse.staleCount}</div><div className="text-xs text-slate-500">Stale</div></div>
              </div>
            ) : null}
          </div>
        )}

        {/* Employee Changes */}
        {empChanges && empChanges.summary && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center justify-between">
              <span>Headcount Changes ({empDays} days)</span>
              <div className="flex gap-1 items-center">
                <button
                  onClick={() => {
                    const allChanges = empChanges.changes ?? [];
                    const filtered = empFilter === 'all' ? allChanges : allChanges.filter(c => c.changeType === empFilter);
                    exportToCSV('headcount-changes', ['Type', 'Name', 'Job Title', 'Date'], filtered.map(c => [
                      c.changeType === 'new_hire' ? 'Hire' : c.changeType === 'departure' || c.changeType === 'termination' ? 'Term' : 'Change',
                      c.name || c.title || '',
                      c.jobTitle || '',
                      formatDate(c.hireDate || c.createdDate),
                    ]));
                  }}
                  className="text-slate-400 hover:text-baker-red text-xs px-1.5 py-0.5 rounded hover:bg-slate-50 transition-all mr-1"
                  title="Export to CSV"
                >📥</button>
                {[30, 60, 90].map(d => (
                  <button key={d} onClick={() => { setEmpDays(d); fetchEmpChanges(d); }} className={`px-2 py-0.5 rounded text-[11px] font-medium ${empDays === d ? 'bg-baker-red text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{d}d</button>
                ))}
              </div>
            </h2>
            {(() => {
              const hires = empChanges.summary.newHires || 0;
              const terms = empChanges.summary.terminations ?? empChanges.summary.departures ?? 0;
              const chgs = empChanges.summary.changes ?? empChanges.summary.roleChanges ?? 0;
              const allChanges = empChanges.changes ?? [];
              const filtered = empFilter === 'all' ? allChanges : allChanges.filter(c => c.changeType === empFilter);

              return (
                <>
                  <div className="flex gap-6 mb-3">
                    <div className={`text-center cursor-pointer rounded-lg p-2 transition-all ${empFilter === 'new_hire' ? 'bg-emerald-50 ring-2 ring-emerald-300' : 'hover:bg-emerald-50/50'}`} onClick={() => setEmpFilter(empFilter === 'new_hire' ? 'all' : 'new_hire')}>
                      <div className="text-2xl font-bold text-emerald-600">{hires}</div>
                      <div className="text-xs text-slate-500">New Hires</div>
                    </div>
                    <div className={`text-center cursor-pointer rounded-lg p-2 transition-all ${empFilter === 'termination' ? 'bg-red-50 ring-2 ring-red-300' : 'hover:bg-red-50/50'}`} onClick={() => setEmpFilter(empFilter === 'termination' ? 'all' : 'termination')}>
                      <div className="text-2xl font-bold text-red-600">{terms}</div>
                      <div className="text-xs text-slate-500">Departures</div>
                    </div>
                    <div className={`text-center cursor-pointer rounded-lg p-2 transition-all ${empFilter === 'change' ? 'bg-amber-50 ring-2 ring-amber-300' : 'hover:bg-amber-50/50'}`} onClick={() => setEmpFilter(empFilter === 'change' ? 'all' : 'change')}>
                      <div className="text-2xl font-bold text-amber-600">{chgs}</div>
                      <div className="text-xs text-slate-500">Changes</div>
                    </div>
                  </div>
                  {filtered.length > 0 && (
                    <div className="border-t border-border pt-2 space-y-1 max-h-64 overflow-y-auto">
                      {empFilter !== 'all' && <div className="text-xs text-slate-400 mb-1 sticky top-0 bg-white">Showing: {empFilter === 'new_hire' ? 'New Hires' : empFilter === 'termination' ? 'Departures' : 'Changes'} ({filtered.length})</div>}
                      {filtered.map((c, i) => (
                        <div key={c.ticketId ?? c.employeeId ?? i} className="flex items-center gap-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${c.changeType === 'new_hire' ? 'bg-emerald-100 text-emerald-700' : c.changeType === 'departure' || c.changeType === 'termination' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.changeType === 'new_hire' ? 'Hire' : c.changeType === 'departure' || c.changeType === 'termination' ? 'Term' : 'Change'}
                          </span>
                          {c.employeeId ? (
                            <Link to={`/employees/${c.employeeId}`} className="text-baker-red no-underline hover:underline font-medium truncate flex-1">{c.name || c.title || '—'}</Link>
                          ) : (
                            <span className="text-slate-700 font-medium truncate flex-1">{c.name || c.title || '—'}</span>
                          )}
                          <span className="text-slate-500 text-[11px] shrink-0">{c.jobTitle || ''}</span>
                          <span className="text-slate-400 text-[11px] shrink-0">{formatDate(c.hireDate || c.createdDate)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">Click a number to filter • Click again to show all</p>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Fleet Health + Mobile Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* NinjaOne */}
        <Link to="/assets?filter=online" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Computer Health (NinjaOne)</h2>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-emerald-600">{fmtNum(assets.ninjaOnline)}</div>
                <div className="text-xs text-slate-500">Online</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-red-600">{fmtNum(assets.ninjaOffline)}</div>
                <div className="text-xs text-slate-500">Offline</div>
              </div>
            </div>
            {assets.ninjaOnline + assets.ninjaOffline > 0 && (
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(assets.ninjaOnline / (assets.ninjaOnline + assets.ninjaOffline)) * 100}%` }} />
              </div>
            )}
          </div>
        </Link>

        {/* Intune */}
        <Link to="/assets?filter=noncompliant" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Intune Compliance</h2>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-emerald-600">{fmtNum(assets.intuneCompliant)}</div>
                <div className="text-xs text-slate-500">Compliant</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-red-600">{fmtNum(assets.intuneNonCompliant)}</div>
                <div className="text-xs text-slate-500">Non-Compliant</div>
              </div>
            </div>
            {assets.intuneCompliant + assets.intuneNonCompliant > 0 && (
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(assets.intuneCompliant / (assets.intuneCompliant + assets.intuneNonCompliant)) * 100}%` }} />
              </div>
            )}
          </div>
        </Link>

        {/* Mobile / SOTI */}
        <Link to="/assets" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Mobile Devices (SOTI)</h2>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">{fmtNum(mobile?.total ?? 0)}</div>
                <div className="text-xs text-slate-500">Total Devices</div>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Fleet Refresh + Data Validation + Branch Health */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Risk Alerts */}
        <Link to="/vendors/risk-alerts" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Vendor Risk Alerts (OSINT)</h2>
            <RiskAlertsSummary />
          </div>
        </Link>

        {/* Fleet Refresh Status */}
        <Link to="/assets/fleet-analytics" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Fleet Refresh Status</h2>
            {fleet?.summary ? (
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{fleet.summary.overdueRefresh}</div>
                  <div className="text-xs text-slate-500">Overdue</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-text-primary">{fleet.summary.avgAge ? `${fleet.summary.avgAge.toFixed(1)}yr` : '—'}</div>
                  <div className="text-xs text-slate-500">Avg Age</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-text-primary">{fleet.summary.totalComputers}</div>
                  <div className="text-xs text-slate-500">Fleet Size</div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">Loading...</p>
            )}
            <p className="text-xs text-slate-400 mt-3">Click for full analytics →</p>
          </div>
        </Link>

        {/* Top 5 Branches by Fleet Health */}
        <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
          <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center justify-between">
            <Link to="/assets/fleet-analytics" className="no-underline text-text-primary hover:text-baker-red">Top 5 Branches by Fleet Health Risk</Link>
            {branchHealth.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); exportToCSV('branch-fleet-health', ['Branch', 'Devices', 'Avg Age', 'Score'], branchHealth.map(b => [b.branchName || b.siteNumber, String(b.deviceCount), String(b.avgAge), String(b.avgScore)])); }}
                className="text-slate-400 hover:text-baker-red text-xs px-1.5 py-0.5 rounded hover:bg-slate-50 transition-all"
                title="Export to CSV"
              >📥</button>
            )}
          </h2>
            {branchHealth.length > 0 ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="pb-1.5 font-medium">Branch</th>
                    <th className="pb-1.5 font-medium text-right">Devices</th>
                    <th className="pb-1.5 font-medium text-right">Avg Age</th>
                    <th className="pb-1.5 font-medium text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {branchHealth.map((b) => (
                    <tr key={b.siteNumber} className="border-t border-border/50">
                      <td className="py-1.5 text-slate-700 font-medium truncate max-w-[140px]" title={b.branchName}>{b.branchName || b.siteNumber}</td>
                      <td className="py-1.5 text-right text-slate-600">{b.deviceCount}</td>
                      <td className="py-1.5 text-right text-slate-600">{b.avgAge}yr</td>
                      <td className="py-1.5 text-right">
                        <span className={`font-bold ${b.avgScore >= 40 ? 'text-red-600' : b.avgScore >= 25 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {b.avgScore}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-400">Loading...</p>
            )}
            <p className="text-xs text-slate-400 mt-3">Higher score = worse health →</p>
          </div>

        {/* Data Validation */}
        <Link to="/admin/data-validation" className="no-underline">
          <div className="bg-white rounded-lg border border-border p-5 hover:border-baker-accent/40 transition-all h-full">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Data Validation Alerts</h2>
            <div className="flex gap-6">
              {dataValidation.highCount > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{dataValidation.highCount}</div>
                  <div className="text-xs text-slate-500">High</div>
                </div>
              )}
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{dataValidation.mediumCount}</div>
                <div className="text-xs text-slate-500">Medium</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-600">{dataValidation.lowCount}</div>
                <div className="text-xs text-slate-500">Low</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Click for full report →</p>
          </div>
        </Link>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-lg border border-border p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">Quick Links</h2>
        <div className="flex flex-wrap gap-3">
          <QuickLink to="/assets/fleet-analytics" label="Fleet Analytics" />
          <QuickLink to="/admin/data-validation" label="Data Validation" />
          <QuickLink to="/admin/import" label="Data Import" />
          <QuickLink to="/circuits/bandwidth" label="Bandwidth" />
        </div>
      </div>

      {/* External Links */}
      <div className="bg-white rounded-lg border border-border p-5">
        <h2 className="text-sm font-semibold text-text-primary mb-3">External Systems</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <ExtLink href="https://support.bakerdist.com/TDNext/Home/Desktop/Default.aspx" label="TDX" sub="Ticketing" />
          <ExtLink href="https://dashboard.meraki.com/o/715461/manage/organization/overview" label="Meraki" sub="Network" />
          <ExtLink href="https://app.ninjarmm.com" label="NinjaOne" sub="Endpoints" />
          <ExtLink href="https://intune.microsoft.com" label="Intune" sub="Compliance" />
          <ExtLink href="https://zoom.us/signin#/phone" label="Zoom Phone" sub="Call Queue" />
        </div>
      </div>
    </div>
  );
}

function TrendArrow({ current, previous, invert }: { current: number; previous: number; invert?: boolean }) {
  if (previous === 0 && current === 0) return null;
  if (current === previous) return null;
  const isUp = current > previous;
  const color = invert ? (isUp ? 'text-emerald-500' : 'text-red-500') : (isUp ? 'text-red-500' : 'text-emerald-500');
  return <span className={`text-[10px] ${color}`}>{isUp ? '↑' : '↓'}</span>;
}

function KPI({ label, value, sub, link }: { label: string; value: string; sub?: string; link?: string }) {
  const content = (
    <div className="bg-white rounded-lg border border-border p-4 hover:border-baker-accent/40 transition-all">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-text-primary mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
  return link ? <Link to={link} className="no-underline">{content}</Link> : content;
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="px-3 py-1.5 rounded-lg border border-border bg-page text-xs font-medium text-slate-600 no-underline hover:border-baker-accent/40 hover:text-baker-red transition-all">
      {label}
    </Link>
  );
}

function RiskAlertsSummary() {
  const [alerts, setAlerts] = useState<Array<{ VendorName: string; SignalType: string; Severity: string; Summary: string; DetectionDate: string }> | null>(null);

  useEffect(() => {
    fetch('/api/vendors/risk-alerts/recent', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setAlerts)
      .catch(() => setAlerts([]));
  }, []);

  if (!alerts) return <p className="text-sm text-slate-400">Loading...</p>;
  if (alerts.length === 0) return <p className="text-sm text-slate-400">No recent risk alerts.</p>;

  return (
    <div className="space-y-2">
      {alerts.slice(0, 5).map((a, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${a.Severity === 'High' ? 'bg-red-100 text-red-700' : a.Severity === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
            {a.Severity}
          </span>
          <div className="flex-1 min-w-0">
            <span className="font-medium">{a.VendorName}</span>
            <span className="text-slate-400"> — {a.SignalType}</span>
          </div>
        </div>
      ))}
      {alerts.length > 5 && <p className="text-xs text-slate-400">+{alerts.length - 5} more</p>}
    </div>
  );
}

function ExtLink({ href, label, sub }: { href: string; label: string; sub: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-page no-underline hover:border-baker-accent/40 hover:shadow-sm transition-all">
      <div>
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <div className="text-[10px] text-slate-400">{sub}</div>
      </div>
      <svg className="w-3.5 h-3.5 text-slate-300 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
    </a>
  );
}
